import React from 'react';
import { Btn } from 'shared/ui/Btn';
import { Picker } from 'shared/ui/Picker';
import { cn } from 'shared/lib/cn';
import { U_PX } from 'shared/lib/rackLayout';
import { useRackView } from './useRackView';
import { RackUnit } from './RackUnit';
import { RackInspector } from './RackInspector';

function Ruler({ size }: { size: number }) {
  return (
    <div data-testid="rack-ruler" className="mr-1 flex w-6 flex-col">
      {Array.from({ length: size }, (_, i) => (
        // P4: ink-muted takes no alpha suffix — the mute is a baked alpha already.
        // R2: top-down, matching RealisticRackVisualization.jsx's own markers
        // (`${rackSize - i}U`, top-anchored) — row 0 reads the rack's highest number.
        <span key={i} style={{ height: U_PX }} className="text-right font-code text-tiny leading-[18px] text-ink-muted">{size - i}</span>
      ))}
    </div>
  );
}

export function RackView() {
  const r = useRackView();
  // R6: "No Rack" still packs at 42 internally (toRackModels never changes what it packs
  // at), but classic hides the frame, rails and unit markers once the user picks it
  // (RealisticRackVisualization.jsx:218-231) — parity with what ships.
  const showFrame = r.rackSize !== '0';

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        {/* shared/ui/Picker is the one Field+Select+sr-only mirror primitive (P7);
            it emits the rackSize-options / rackSize-native test ids directly. */}
        <Picker
          id="rackSize"
          label="Rack Size"
          options={r.rackSizeOptions}
          value={r.rackSize}
          onChange={r.onRackSizeChange}
          className="w-40"
        />
        <div className="flex gap-2">
          <Btn variant="outlined" size="sm" aria-label="Add director node" onClick={() => r.addNode('d')}>+ D</Btn>
          <Btn variant="outlined" size="sm" aria-label="Add flash node" onClick={() => r.addNode('f')}>+ F</Btn>
          <Btn variant="outlined" size="sm" aria-label="Add storage node" onClick={() => r.addNode('s')}>+ S</Btn>
        </div>
      </div>

      <div className="flex flex-1 gap-4 overflow-auto">
        <div className="flex gap-6">
          {r.racks.map((rack) => (
            <div key={rack.index} className="flex">
              {showFrame && <Ruler size={rack.size} />}
              <div
                className={cn(
                  'relative w-40',
                  // R5: a Tailwind `border` grows the box under border-box preflight, so
                  // the frame's content box (size*U_PX − 2) drifts 1px from the borderless
                  // Ruler's ticks. An inset ring paints inside the box instead — exact height.
                  showFrame && 'rounded-[var(--radius-2)] ring-1 ring-inset ring-hairline/25 bg-surface-raised',
                )}
                style={{ height: rack.size * U_PX }}
              >
                {rack.units.map(({ position, unit }) => (
                  // R2: top-down — index 0 (the first slot calculateRackLayout fills) is the
                  // TOP of the rack in both contractual consumers (RealisticRackVisualization
                  // .jsx:198 `top: index * unitHeight`; rackSlidePPTGenerator.js:151
                  // `y = slotIndex * unitH`, SVG y grows downward), not the bottom.
                  <div key={`${rack.index}-${position}`} className="absolute inset-x-0" style={{ top: position * U_PX }}>
                    <RackUnit
                      unit={unit}
                      vendorId={r.vendorId}
                      photographic={r.isPhotographic}
                      selected={r.selected?.label === unit.label}
                      onSelect={() => r.select(unit)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <RackInspector unit={r.selected} />
      </div>
    </div>
  );
}
